// Planos e períodos de teste. A cobrança de verdade é pela Google Play e pela App Store (lib/compras.ts, 0.64).
//   Básico (My Planner + Anotações): grátis por 6 meses a partir da criação da conta; depois US$ 10 por ano.
//   Cada aba extra: 10 dias grátis a partir do primeiro uso; depois US$ 5 por ano.
// Quem tem acesso ilimitado (liberado pelo CPF) ou aba liberada pelo suporte não passa por isso.
import type { AbaId, Ajustes } from '../store/types';
import { getLiberacao, type Liberacao } from '../store/store';
import { addDays, parseYmd, today } from './date';
import { dinheiro } from './moeda';
import { MODO_TESTE } from './compras';
import { DEMO } from './env';

/** Assinada pela loja e ainda no prazo, ou (só no modo de teste) pela chave de simulação. */
export function assinada(id: AbaId, aj: Ajustes, lib: Liberacao): boolean {
  const ate = lib.loja?.[id];
  if (ate && ate >= new Date().toISOString().slice(0, 10)) return true;
  return (MODO_TESTE || DEMO) && !!aj.assinaturas?.[id];
}
/** Data em que a assinatura da loja renova ou termina (YYYY-MM-DD), ou null. */
export const assinadaAte = (id: AbaId, lib: Liberacao) => { const a = lib.loja?.[id]; return a && !a.startsWith('9999') ? a.slice(0, 10) : null; };

/** Lançamento grátis nas lojas (preparado na 0.54, adiado na 0.55). Com false, todas as abas ficam liberadas
 *  (sem teste e sem bloqueio) e nenhuma tela fala em assinatura. Com true, vale o plano normal com os testes grátis. */
export const COBRANCA_ATIVA = false;   // 0.73: lançamento nas lojas grátis (a cobrança é ligada depois, pela atualização automática)
/** Cobrança valendo PARA ESTA CONTA: a regra geral acima ou a conta de teste de compras (0.72, texto 10 C10:
 *  marcada no servidor pelo 47-CONTA-DE-TESTE.bat). Use esta função nas telas, não a constante. */
export const cobrancaAtiva = () => COBRANCA_ATIVA || !!getLiberacao().teste;
export const ehContaTeste = () => !!getLiberacao().teste;

export const PRECO_BASICO_USD = 10;
export const PRECO_ABA_USD = 5;
export const DIAS_TESTE_ABA = 10;
export const MESES_TESTE_BASICO = 6;

let contaCriadaEm: string | null = null;
/** Chamado ao entrar: data de criação da conta (YYYY-MM-DD). */
export const setContaCriadaEm = (iso: string | null | undefined) => { contaCriadaEm = iso ? iso.slice(0, 10) : null; };
export const contaCriada = () => contaCriadaEm;
/** Conta nova (até 30 dias): ainda pode usar o código de quem indicou. */
export const contaNova = () => !contaCriadaEm || dias(contaCriadaEm, today()) <= 30;

/** Bônus da indicação: todas as abas liberadas até a data (0.32). */
function bonus(aj: Ajustes): Situacao | null {
  if (!aj.bonusAte) return null;
  const restam = dias(today(), aj.bonusAte);
  return restam >= 0 ? { tipo: 'teste', restam, ate: aj.bonusAte } : null;
}

const dias = (de: string, ate: string) => Math.round((parseYmd(ate).getTime() - parseYmd(de).getTime()) / 864e5);
function somaMeses(s: string, n: number) { const d = parseYmd(s); d.setMonth(d.getMonth() + n); const p = (x: number) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; }

export type Situacao =
  | { tipo: 'liberada'; motivo: 'ilimitado' | 'suporte' | 'assinatura' | 'lancamento' }
  | { tipo: 'teste'; restam: number; ate: string }
  | { tipo: 'teste-disponivel' }
  | { tipo: 'vencida'; desde: string };

/** Situação do plano básico (My Planner + Anotações). */
export function situacaoBasico(aj: Ajustes, lib: Liberacao): Situacao {
  if (lib.ilimitado) return { tipo: 'liberada', motivo: 'ilimitado' };
  if (assinada('planner', aj, lib)) return { tipo: 'liberada', motivo: 'assinatura' };
  if (!cobrancaAtiva()) return { tipo: 'liberada', motivo: 'lancamento' };
  const ini = aj.testes?.basico ?? contaCriadaEm ?? today();
  const ate = somaMeses(ini, MESES_TESTE_BASICO);
  const restam = dias(today(), ate);
  return restam >= 0 ? { tipo: 'teste', restam, ate } : { tipo: 'vencida', desde: ate };
}

/** Situação de uma aba paga. */
export function situacaoAba(id: AbaId, aj: Ajustes, lib: Liberacao, doSuporte: string[]): Situacao {
  if (lib.ilimitado) return { tipo: 'liberada', motivo: 'ilimitado' };
  if (doSuporte.includes(id)) return { tipo: 'liberada', motivo: 'suporte' };
  if (assinada(id, aj, lib)) return { tipo: 'liberada', motivo: 'assinatura' };
  if (!cobrancaAtiva()) return { tipo: 'liberada', motivo: 'lancamento' };
  const ini = aj.testes?.[id];
  const b = bonus(aj);
  if (!ini) return b ?? { tipo: 'teste-disponivel' };
  const ate = addDays(ini, DIAS_TESTE_ABA - 1);
  const restam = dias(today(), ate);
  if (b && b.tipo === 'teste' && (restam < 0 || b.restam > restam)) return b;
  return restam >= 0 ? { tipo: 'teste', restam, ate } : { tipo: 'vencida', desde: ate };
}
export const podeUsar = (s: Situacao) => s.tipo === 'liberada' || s.tipo === 'teste';

// Conversão aproximada só para mostrar o preço (a loja cobra o valor oficial na moeda do país).
const POR_USD: Record<string, number> = { USD: 1, BRL: 5.4, EUR: 0.92, GBP: 0.78, CAD: 1.37, AUD: 1.52, NZD: 1.66, CHF: 0.88, JPY: 150, CNY: 7.2, HKD: 7.8,
  TWD: 32, KRW: 1380, INR: 84, BDT: 120, PKR: 280, IDR: 16000, MYR: 4.5, SGD: 1.34, THB: 35, PHP: 57, VND: 25000, RUB: 95, UAH: 41, PLN: 4, CZK: 23,
  SEK: 10.5, NOK: 10.8, DKK: 6.9, TRY: 34, ILS: 3.7, SAR: 3.75, AED: 3.67, EGP: 48, ZAR: 18, NGN: 1600, KES: 129, MAD: 10, AOA: 900, MZN: 64,
  ARS: 1000, CLP: 950, COP: 4100, MXN: 18, PEN: 3.8, UYU: 40, PYG: 7700, BOB: 6.9, VES: 40 };
/** "US$ 10,00 por ano (≈ R$ 54,00)" */
export function precoTexto(usd: number, moeda: string) {
  const base = dinheiro(usd, 'USD');
  const taxa = POR_USD[moeda];
  return moeda !== 'USD' && taxa ? `${base} (≈ ${dinheiro(Math.round(usd * taxa * 100) / 100, moeda)})` : base;
}
